import { supabase } from "@/integrations/supabase/client";

/**
 * One place to create a support ticket and hand it to the help desk.
 *
 * Why this exists: the portal used to insert a ticket, then do more work that
 * could fail. When that later work failed, the member saw an error, tried
 * again, and every attempt left a brand new ticket already sent to the help
 * desk. One message arrived as four or five tickets.
 *
 * The guard is an idempotency key. The caller holds one key for the whole
 * submission (including retries), the database has a unique rule on it, and a
 * repeat attempt returns the ticket that already exists instead of making a
 * new one.
 */

export interface SupportTicketInput {
  userId: string;
  subject: string;
  message: string;
  category?: string;
  priority?: string;
  /** Stable across retries of the same submission. */
  idempotencyKey: string;
}

export interface SupportTicketResult {
  id: string;
  /** True when this call reused a ticket an earlier attempt had created. */
  reused: boolean;
}

/** A fresh key for one submission. Keep it until the submission succeeds. */
export function newSubmissionKey(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export async function createSupportTicket(
  input: SupportTicketInput,
): Promise<SupportTicketResult> {
  const row = {
    user_id: input.userId,
    subject: input.subject,
    message: input.message,
    category: input.category ?? "General",
    priority: input.priority ?? "medium",
    idempotency_key: input.idempotencyKey,
  };

  // Look first: on a retry the row is already there, and a plain read avoids
  // burning a sequence value or tripping the unique rule.
  const { data: existing } = await supabase
    .from("support_tickets")
    .select("id")
    .eq("idempotency_key", input.idempotencyKey)
    .maybeSingle();

  if (existing?.id) return { id: existing.id, reused: true };

  const { data, error } = await supabase
    .from("support_tickets")
    .insert(row as never)
    .select("id")
    .single();

  if (error) {
    // Two clicks landed at the same moment. Take whichever one won.
    if ((error as { code?: string }).code === "23505") {
      const { data: raced } = await supabase
        .from("support_tickets")
        .select("id")
        .eq("idempotency_key", input.idempotencyKey)
        .maybeSingle();
      if (raced?.id) return { id: raced.id, reused: true };
    }
    throw error;
  }

  return { id: data.id, reused: false };
}

/**
 * Hand a ticket to the help desk. Safe to call more than once for the same
 * ticket: the edge function refuses to make a second conversation.
 */
export function syncTicketToHelpDesk(body: {
  ticketId: string;
  subject: string;
  message: string;
  userEmail: string;
  userName: string;
  category?: string;
  priority?: string;
  chatHistory?: Array<{ role: string; content: string }>;
}) {
  return supabase.functions
    .invoke("helpscout-ticket", { body })
    .catch((e) => console.error("helpscout-ticket sync failed:", e));
}
